import {
  KnownSortOrder,
  OrderDto,
  OrdersControllerOrdersListOptionalParams,
  ReportsControllerRetailDeliveriesOptionalParams
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useApi } from '../../core/api/api.context';
import { useInfiniteQuery, useQueries, useQuery } from 'react-query';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import { RestError } from '@azure/core-http';
import { CustomOrderStatusOptions, OptionsToValuesMap } from './orders.constants';
import { useTimelineUpdatesEventCentre } from '../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { useCallback, useMemo } from 'react';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionStream
} from '../../events/timelineUpdates/timelineUpdates.types';
type MaterialUiPickersDate = import('dayjs').Dayjs | null;

export function serializeQueryParams({
  fromDate,
  sortBy,
  sortOrder,
  status,
  toDate,
  searchTerm,
  customerID,
  hubID
}: Partial<UseQueryListOrdersParams>): OrdersControllerOrdersListOptionalParams {
  return {
    deliveredTo: toDate ? toDate.toDate() : undefined,
    deliveredFrom: fromDate ? fromDate.toDate() : undefined,
    statuses:
      status && (status === 'not-delivered' || status === 'completed' || status === 'current-deliveries-category')
        ? OptionsToValuesMap[status as CustomOrderStatusOptions]
        : status
        ? [status]
        : undefined,
    status: undefined,
    searchTerm: searchTerm || undefined,
    sortBy,
    sortOrder,
    customerID,
    hubID: hubID || undefined
  };
}

export type UseQueryListOrdersParams = {
  sortBy: string;
  sortOrder: KnownSortOrder;
  status: string;
  fromDate: MaterialUiPickersDate;
  toDate: MaterialUiPickersDate;
  searchTerm: string;
  customerID?: string;
  hubID?: string | null;
};

export function useInfiniteQueryOrdersList(
  retailerID: string | undefined,
  page: number,
  params: Partial<UseQueryListOrdersParams>
) {
  const api = useApi();
  const queryParams = serializeQueryParams(params);

  return useInfiniteQuery(
    [QueryKeys.ORDERS, retailerID, page, queryParams],
    async ({ pageParam = 1 }) => {
      if (retailerID === undefined) {
        return {
          items: [],
          totalRecords: 0,
          perPageRecords: 25,
          totalPages: 1,
          pageNo: 1
        };
      }
      const result = await api.ordersController.ordersList(retailerID, {
        ...queryParams,
        pageNo: pageParam,
        perPageRecords: 25,
        includeChildren: true,
        includeNotifications: true,
        maxNotifications: 1
      });

      return { ...result, items: result.items || [] };
    },
    {
      enabled: !!retailerID,
      // FYI: when getNextPageParam returns undefined, the value of hasNextPage will be false
      getNextPageParam(lastPage) {
        if (lastPage.pageNo < lastPage.totalPages) {
          return lastPage.pageNo + 1;
        } else {
          return undefined;
        }
      }
    }
  );
}

export const useQueryGetOrder = (retailerID: string | undefined, orderId: string) => {
  const api = useApi();

  const query = useQuery<OrderDto | undefined, RestError>([QueryKeys.ORDERS, retailerID, orderId], async () => {
    if (retailerID === undefined) {
      return undefined;
    }
    return await api.ordersController.ordersGet(retailerID, orderId);
  });

  // subscribe to timeline update events
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    query.refetch();
  }, [query]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      return event.data.workflowID === orderId;
    },
    [orderId]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [],
      workflowIDs: [orderId],
      projectIDs: []
    }),
    [orderId]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  return query;
};

export const useQueryGetOrders = (ids: string[], projectID: string) => {
  const api = useApi();
  const queries = ids.map((id) => ({
    queryKey: [QueryKeys.ORDERS, projectID, id],
    queryFn: () => api.ordersController.ordersGet(projectID, id)
  }));
  return useQueries(queries);
};

/**
 * The orders list filters that the deliveries report accepts. Search and sort only apply to the list.
 */
export function serializeReportQueryParams(
  params: Partial<UseQueryListOrdersParams>
): ReportsControllerRetailDeliveriesOptionalParams {
  const { statuses, hubID, deliveredFrom, deliveredTo } = serializeQueryParams(params);
  return { statuses, hubID, deliveredFrom, deliveredTo };
}

export function useQueryGetDeliveriesReport(
  onSuccess: (response: string) => void,
  projectID: string,
  params: Partial<UseQueryListOrdersParams>
) {
  const api = useApi();
  const queryParams = serializeReportQueryParams(params);

  return useQuery(
    [QueryKeys.REPORTS, QueryKeys.DELIVERIES_REPORT, projectID, queryParams],
    async () => {
      // The CSV is a text/csv stream in the spec. The SDK types that response as `body`, but
      // core-client returns a stream response as `blobBody` and never sets `body`.
      const result = (await api.reportsController.retailDeliveries(projectID, queryParams)) as unknown as {
        blobBody?: Promise<Blob>;
      };
      const blob = await result.blobBody;
      if (!blob) {
        throw new Error('The deliveries report response has no body');
      }
      return await blob.text();
    },
    {
      onSuccess,
      cacheTime: 0
    }
  );
}
